import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectTherapistProfile,
  selectTherapistId,
  selectClinicId,
  setTherapistProfile,
  selectUser,
  setUser,
  selectIsNutritionClinic,
} from "../../store/slices/authSlice";
import { signInWithCustomToken } from "firebase/auth";
import {
  auth,
  updateTherapistDocument,
  linkEmailToAuthUser,
  DEFAULT_PATIENT_TYPES,
  isPushNotificationSupported,
  getPushPermissionState,
  requestPushPermissionAndSaveToken,
  disablePushNotifications,
  sendTestPushNotification,
} from "../../firebase";
import { useToast } from "../../contexts/ToastContext";
import {
  UserCircle,
  Bell,
  CheckCircle,
  CalendarCheck,
  FileText,
  ListChecks,
  Warning,
  CurrencyCircleDollar,
  House,
  ArrowUp,
  ArrowDown,
  UserPlus,
  Plus,
  X,
  Pencil,
  Trash,
  UsersThree,
  Tag,
  ChatText,
  BellRinging,
  DeviceMobile,
  EnvelopeSimple,
  PaperPlaneTilt,
  PenNib,
} from "@phosphor-icons/react";
import { deleteField, serverTimestamp } from "firebase/firestore";
import SignaturePadModal from "../../components/ui/SignaturePadModal";
import { HelpButton } from "../../components/shared";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import ModalCloseButton from "../../components/ui/ModalCloseButton";
import "./SettingsPage.css";

// ============================================
// NOTIFICATION PREFERENCES SCHEMA
// ============================================

/**
 * Notification preference keys and their default states
 * - enabled: true = notification is ON by default
 * - enabled: false = notification is OFF by default (user can enable)
 */
export const NOTIFICATION_PREFERENCES = {
  // High priority - ON by default
  appointmentConfirmed: {
    key: "appointmentConfirmed",
    label: "מטופל אישר פגישה",
    description: "התראה כשמטופל מאשר הגעה דרך קישור האישור",
    category: "appointments",
    defaultEnabled: true,
    urgency: "low",
  },
  appointmentDeclined: {
    key: "appointmentDeclined",
    label: "מטופל דחה פגישה",
    description: "התראה דחופה כשמטופל מבטל את הפגישה",
    category: "appointments",
    defaultEnabled: true,
    urgency: "high",
  },
  appointmentScheduledByAdmin: {
    key: "appointmentScheduledByAdmin",
    label: "נקבעה פגישה עבורך",
    description: "התראה כשמנהל הקליניקה קובע עבורך פגישה",
    category: "appointments",
    defaultEnabled: true,
    urgency: "medium",
  },
  bookingRequestReceived: {
    key: "bookingRequestReceived",
    label: "בקשת תור חדשה",
    description: "התראה כשמטופל מבקש תור דרך דף ההזמנות הציבורי",
    category: "appointments",
    defaultEnabled: true,
    urgency: "high",
  },
  appointmentCancelledByAdmin: {
    key: "appointmentCancelledByAdmin",
    label: "פגישה שלך בוטלה",
    description: "התראה כשמנהל הקליניקה מבטל פגישה שנקבעה עבורך",
    category: "appointments",
    defaultEnabled: true,
    urgency: "high",
  },
  appointmentRescheduledByAdmin: {
    key: "appointmentRescheduledByAdmin",
    label: "פגישה שלך עודכנה",
    description: "התראה כשמנהל הקליניקה משנה את מועד הפגישה שלך",
    category: "appointments",
    defaultEnabled: true,
    urgency: "medium",
  },
  smsSendingFailed: {
    key: "smsSendingFailed",
    label: "כשל בשליחת SMS",
    description: "התראה כששליחת הודעת SMS נכשלת",
    category: "system",
    defaultEnabled: true,
    urgency: "high",
  },
  unconfirmedAppointmentReminder: {
    key: "unconfirmedAppointmentReminder",
    label: "פגישה מחר ללא אישור",
    description: "תזכורת יומית ב-18:00 על פגישות מחר שלא אושרו",
    category: "appointments",
    defaultEnabled: true,
    urgency: "medium",
  },
  taskOverdue: {
    key: "taskOverdue",
    label: "משימה פג תוקפה",
    description: "התראה דחופה על משימות שעברו את תאריך היעד",
    category: "tasks",
    defaultEnabled: true,
    urgency: "high",
  },
  taskAssigned: {
    key: "taskAssigned",
    label: "משימה הוקצתה לך",
    description: "התראה כשמשימה מוקצית לך",
    category: "tasks",
    defaultEnabled: true,
    urgency: "medium",
  },
  clinicReminderCreated: {
    key: "clinicReminderCreated",
    label: "תזכורת חדשה בקליניקה",
    description: "התראה כשמנהל מוסיף תזכורת קליניקה חדשה",
    category: "tasks",
    defaultEnabled: true,
    urgency: "medium",
  },
  cloudTaskFailed: {
    key: "cloudTaskFailed",
    label: "כשל במשימה אוטומטית",
    description: "התראה על כשל במשימות אוטומטיות של המערכת",
    category: "system",
    defaultEnabled: true,
    urgency: "high",
  },
  permissionsUpdated: {
    key: "permissionsUpdated",
    label: "עדכון הרשאות",
    description: "התראה כשמנהל/ת הקליניקה משנה את התפקיד או ההרשאות שלך",
    category: "system",
    defaultEnabled: true,
    urgency: "medium",
  },
  teamMemberFirstLogin: {
    key: "teamMemberFirstLogin",
    label: "כניסה ראשונה של איש/אשת צוות",
    description: "התראה למנהלים כשאיש/אשת צוות חדש/ה נכנס/ת למערכת בפעם הראשונה",
    category: "system",
    defaultEnabled: true,
    urgency: "low",
  },
  trialEndingSoon: {
    key: "trialEndingSoon",
    label: "תקופת ניסיון מסתיימת",
    description: "תזכורת לפני סיום תקופת הניסיון",
    category: "system",
    defaultEnabled: true,
    urgency: "medium",
  },

  // Lower priority - OFF by default (user can enable)
  treatmentCommentAdded: {
    key: "treatmentCommentAdded",
    label: "תגובה חדשה לתיעוד טיפול",
    description: "התראה כשמישהו מגיב על תיעוד טיפול שלך",
    category: "treatments",
    defaultEnabled: false,
    urgency: "low",
  },
  treatmentDraftReminder: {
    key: "treatmentDraftReminder",
    label: "טיוטת תיעוד ממתינה",
    description: "תזכורת על תיעודי טיפול בטיוטא (48 שעות)",
    category: "treatments",
    defaultEnabled: false,
    urgency: "low",
  },
  patientOutstandingDebt: {
    key: "patientOutstandingDebt",
    label: "חוב פתוח למטופל",
    description: "תזכורת על מטופלים עם חובות פתוחים",
    category: "finance",
    defaultEnabled: false,
    urgency: "low",
  },
  // Leads v2 (leads_plan.md §7)
  leadInbound: {
    key: "leadInbound",
    label: "מתעניין חדש נקלט",
    description: "התראה כשמתעניין נקלט אוטומטית מהאתר / קמפיין (למטפל/ת המשויך/ת, או למנהלים כשאין שיוך)",
    category: "leads",
    defaultEnabled: true,
    urgency: "medium",
  },
  leadFollowUpDue: {
    key: "leadFollowUpDue",
    label: "מתעניינים לטיפול היום",
    description: "סיכום יומי ב-8:30 של המתעניינים שמועד המעקב שלהם הגיע (כולל באיחור)",
    category: "leads",
    defaultEnabled: true,
    urgency: "low",
  },
  // Grow payments (grow_integration.md Phase 7)
  growPaymentReceived: {
    key: "growPaymentReceived",
    label: "תשלום התקבל דרך Grow",
    description: "התראה כשמטופל משלם דרישת תשלום (למטפל/ת של הפגישה ולמנהלים)",
    category: "finance",
    defaultEnabled: true,
    urgency: "medium",
  },
  growLinkUnpaidReminder: {
    key: "growLinkUnpaidReminder",
    label: "דרישת תשלום שלא שולמה",
    description: "תזכורת יומית ב-9:00 על דרישות תשלום Grow שנשלחו ולא שולמו (לפי ההגדרה בחיבור Grow)",
    category: "finance",
    defaultEnabled: true,
    urgency: "low",
  },
  growPaymentFailed: {
    key: "growPaymentFailed",
    label: "תקלה בתשלום Grow",
    description: "התראה למנהלים על תשלום שנדחה, עדכון שלא תאם או שליחה שנכשלה",
    category: "finance",
    defaultEnabled: true,
    urgency: "high",
  },
  patientBirthday: {
    key: "patientBirthday",
    label: "יום הולדת למטופל",
    description: "תזכורת יומית ב-9:00 (באפליקציה ובפוש) על מטופלים שחוגגים היום יום הולדת",
    category: "patients",
    defaultEnabled: true,
    urgency: "low",
  },
};

// Group notifications by category for UI display
const NOTIFICATION_CATEGORIES = {
  appointments: {
    label: "יומן ופגישות",
    icon: CalendarCheck,
    keys: [
      "appointmentConfirmed",
      "appointmentDeclined",
      "appointmentScheduledByAdmin",
      "appointmentCancelledByAdmin",
      "appointmentRescheduledByAdmin",
      "unconfirmedAppointmentReminder",
    ],
  },
  treatments: {
    label: "תיעוד טיפולים",
    icon: FileText,
    keys: ["treatmentCommentAdded", "treatmentDraftReminder"],
  },
  tasks: {
    label: "משימות ותזכורות",
    icon: ListChecks,
    keys: ["taskOverdue", "taskAssigned", "clinicReminderCreated"],
  },
  finance: {
    label: "כספים",
    icon: CurrencyCircleDollar,
    keys: ["patientOutstandingDebt", "growPaymentReceived", "growLinkUnpaidReminder", "growPaymentFailed"],
  },
  patients: {
    label: "מטופלים",
    icon: UsersThree,
    keys: ["patientBirthday"],
  },
  leads: {
    label: "מתעניינים",
    icon: UserPlus,
    keys: ["leadInbound", "leadFollowUpDue"],
  },
  system: {
    label: "מערכת",
    icon: Warning,
    keys: ["smsSendingFailed", "cloudTaskFailed", "trialEndingSoon"],
  },
};

// ============================================
// SMS NOTIFICATION PREFERENCES SCHEMA
// ============================================

export const SMS_NOTIFICATION_PREFERENCES = {
  appointmentDeclinedViaSms: {
    key: "appointmentDeclinedViaSms",
    label: "ביטול פגישה דרך קישור אישור הגעה",
    description: "קבלת SMS כשמטופל מבטל פגישה דרך קישור אישור ההגעה",
    defaultEnabled: true,
  },
  bookingRequestReceivedViaSms: {
    key: "bookingRequestReceivedViaSms",
    label: "בקשת תור חדשה מדף ההזמנות",
    description: "קבלת SMS כשמטופל מבקש תור דרך דף ההזמנות הציבורי",
    defaultEnabled: true,
  },
};

export const getDefaultSmsNotificationPreferences = () => {
  const defaults = {};
  Object.values(SMS_NOTIFICATION_PREFERENCES).forEach((pref) => {
    defaults[pref.key] = pref.defaultEnabled;
  });
  return defaults;
};

// ============================================
// PUSH NOTIFICATION PREFERENCES SCHEMA
// ============================================

export const PUSH_PREFERENCES = {
  initialConfirmed: {
    key: "initialConfirmed",
    label: "מטופל אישר הגעה (הודעה ראשונית)",
    description: "התראת פוש כשמטופל מאשר הגעה דרך ההודעה הראשונית",
    category: "appointments",
    defaultEnabled: true,
  },
  reminderConfirmed: {
    key: "reminderConfirmed",
    label: "מטופל אישר הגעה (תזכורת)",
    description: "התראת פוש כשמטופל מאשר הגעה דרך הודעת התזכורת",
    category: "appointments",
    defaultEnabled: false,
  },
  declined: {
    key: "declined",
    label: "מטופל ביטל פגישה",
    description: "התראת פוש כשמטופל מבטל פגישה (ראשונית או תזכורת)",
    category: "appointments",
    defaultEnabled: true,
  },
  reminder30min: {
    key: "reminder30min",
    label: "תזכורת 30 דקות לפני פגישה",
    description: "התראת פוש חצי שעה לפני שפגישה מתחילה",
    category: "appointments",
    defaultEnabled: true,
  },
  reminder15min: {
    key: "reminder15min",
    label: "תזכורת 15 דקות לפני פגישה",
    description: "התראת פוש רבע שעה לפני שפגישה מתחילה",
    category: "appointments",
    defaultEnabled: false,
  },
  bookingRequestReceived: {
    key: "bookingRequestReceived",
    label: "בקשת תור חדשה מדף ההזמנות",
    description: "התראת פוש כשמטופל מבקש תור דרך דף ההזמנות הציבורי",
    category: "appointments",
    defaultEnabled: true,
  },
  growPaymentReceived: {
    key: "growPaymentReceived",
    label: "תשלום התקבל דרך Grow",
    description: "התראת פוש כשמטופל משלם דרישת תשלום Grow",
    category: "finance",
    defaultEnabled: true,
  },
  // Leads v2 (leads_plan.md §7)
  leadInbound: {
    key: "leadInbound",
    label: "מתעניין חדש נקלט",
    description: "התראת פוש כשמתעניין נקלט אוטומטית מהאתר / קמפיין",
    category: "leads",
    defaultEnabled: true,
  },
  leadFollowUpDue: {
    key: "leadFollowUpDue",
    label: "מתעניינים לטיפול היום",
    description: "התראת פוש אחת ב-8:30 עם המתעניינים שמועד המעקב שלהם הגיע",
    category: "leads",
    defaultEnabled: true,
  },
  nutritionReportsReady: {
    key: "nutritionReportsReady",
    label: "דוחות אכילה ממתינים לסקירה",
    description: "התראת פוש אחת בבוקר, המסכמת את דוחות האכילה שנסגרו אתמול",
    category: "nutrition",
    defaultEnabled: true,
  },
  taskDueSoon: {
    key: "taskDueSoon",
    label: "משימה מתקרבת לדדליין",
    description: "התראת פוש כשמשימה מתקרבת לתאריך היעד",
    category: "tasks",
    defaultEnabled: false,
  },
  taskOverdue: {
    key: "taskOverdue",
    label: "משימה שעברה דדליין",
    description: "התראת פוש כשמשימה עברה את תאריך היעד",
    category: "tasks",
    defaultEnabled: false,
  },
  formSigned: {
    key: "formSigned",
    label: "מטופל חתם על טופס",
    description: "התראת פוש כשמטופל חותם על טופס דיגיטלי",
    category: "forms",
    defaultEnabled: true,
  },
  questionnaireCompleted: {
    key: "questionnaireCompleted",
    label: "מטופל מילא שאלון",
    description: "התראת פוש כשמטופל משלים מילוי שאלון",
    category: "forms",
    defaultEnabled: true,
  },
  dailySummary: {
    key: "dailySummary",
    label: "סיכום בוקר — הפגישות להיום",
    description: "התראת פוש יומית ב-07:30 עם סיכום הפגישות של היום",
    category: "daily",
    defaultEnabled: true,
  },
  pendingUpdateEvening: {
    key: "pendingUpdateEvening",
    label: "תזכורת ערב — פגישות שממתינות לעדכון",
    description: "התראת פוש יומית ב-19:00 כשמצטברות 5 פגישות או יותר שממתינות לעדכון",
    category: "daily",
    defaultEnabled: true,
  },
  // NOTE: patientBirthday is intentionally NOT here. It is controlled by the
  // single in-app toggle (NOTIFICATION_PREFERENCES.patientBirthday, category
  // "patients"); push follows that same key + the global push switch.
};

const PUSH_PREF_CATEGORIES = {
  appointments: {
    label: "פגישות ואישורים",
    keys: ["initialConfirmed", "reminderConfirmed", "declined", "reminder30min", "reminder15min", "bookingRequestReceived"],
  },
  tasks: {
    label: "משימות",
    keys: ["taskDueSoon", "taskOverdue"],
  },
  forms: {
    label: "טפסים ושאלונים",
    keys: ["formSigned", "questionnaireCompleted"],
  },
  daily: {
    label: "תזכורות יומיות",
    keys: ["dailySummary", "pendingUpdateEvening"],
  },
  // Nutrition clinics only — filtered out of the rendered list everywhere else
  finance: {
    label: "כספים",
    keys: ["growPaymentReceived"],
  },
  leads: {
    label: "מתעניינים",
    keys: ["leadInbound", "leadFollowUpDue"],
  },
  nutrition: {
    label: "תזונה",
    keys: ["nutritionReportsReady"],
    nutritionOnly: true,
  },
};

export const getDefaultPushPreferences = () => {
  const defaults = {};
  Object.values(PUSH_PREFERENCES).forEach((pref) => {
    defaults[pref.key] = pref.defaultEnabled;
  });
  return defaults;
};

// ============================================
// HOME PAGE SETTINGS SCHEMA (COMMENTED OUT)
// ============================================

/**
 * Available stats for the top cards section
 */
export const HOMEPAGE_STATS_OPTIONS = {
  todayAppointments: {
    id: "todayAppointments",
    label: "פגישות היום",
    icon: "Calendar",
    color: "primary",
  },
  activePatients: {
    id: "activePatients",
    label: "מטופלים פעילים",
    icon: "Users",
    color: "purple",
  },
  openTasks: {
    id: "openTasks",
    label: "משימות פתוחות",
    icon: "CheckSquare",
    color: "yellow",
  },
  monthlyIncome: {
    id: "monthlyIncome",
    label: "הכנסות החודש",
    icon: "CurrencyCircleDollar",
    color: "green",
  },
  futureAppointments: {
    id: "futureAppointments",
    label: "פגישות עתידיות החודש",
    icon: "CalendarCheck",
    color: "primary",
  },
  pendingCollection: {
    id: "pendingCollection",
    label: "ממתין לגבייה",
    icon: "Hourglass",
    color: "yellow",
  },
};

/**
 * Available sections for the home page (excluding locked ones)
 */
export const HOMEPAGE_SECTIONS = {
  notifications: {
    id: "notifications",
    label: "התראות",
    icon: Bell,
    description: "התראות חדשות שלא נקראו",
  },
  appointmentsBar: {
    id: "appointmentsBar",
    label: "פגישות (היום + הבאות)",
    icon: CalendarCheck,
    description: "רשימת הפגישות של היום והפגישות הקרובות",
  },
  tasks: {
    id: "tasks",
    label: "משימות",
    icon: ListChecks,
    description: "משימות פתוחות וסטטוס שלהן",
  },
  reminders: {
    id: "reminders",
    label: "תזכורות",
    icon: FileText,
    description: "תזכורות אישיות ומידע חשוב",
  },
  leads: {
    id: "leads",
    label: "ניהול מתעניינים",
    icon: UsersThree,
    description: "רשימת מתעניינים פעילים וסטטוס שלהם",
  },
  quickActions: {
    id: "quickActions",
    label: "פעולות מהירות",
    icon: House,
    description: "כפתורי גישה מהירה לפעולות נפוצות",
  },
};

/**
 * Default home page settings
 */
export const getDefaultHomePageSettings = () => ({
  // Stats selection (which 6 stats to show)
  selectedStats: [
    "todayAppointments",
    "activePatients",
    "openTasks",
    "monthlyIncome",
    "futureAppointments",
    "pendingCollection",
  ],
  // Sections visibility and order
  sections: [
    { id: "notifications", visible: true },
    { id: "appointmentsBar", visible: true },
    { id: "tasks", visible: true },
    { id: "reminders", visible: true },
    { id: "leads", visible: true },
    { id: "quickActions", visible: true },
  ],
});

/**
 * Get default notification preferences object
 * @returns {Object} Default preferences with all notifications set to their default state
 */
export const getDefaultNotificationPreferences = () => {
  const defaults = {};
  Object.values(NOTIFICATION_PREFERENCES).forEach((pref) => {
    defaults[pref.key] = pref.defaultEnabled;
  });
  return defaults;
};

const EMAIL_LINK_ALLOWED_PHONES = [
  "+972508158072",
  "+972528776086",
  "+972547778839",
];

function SettingsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const therapistProfile = useSelector(selectTherapistProfile);
  const therapistId = useSelector(selectTherapistId);
  // Gates the nutrition push-preference category
  const isNutritionClinic = useSelector(selectIsNutritionClinic);

  // ─── Saved therapist signature (reused on signed PDFs) ───
  const [signaturePadOpen, setSignaturePadOpen] = useState(false);
  const [signatureBusy, setSignatureBusy] = useState(false);
  const savedSignatureUrl = therapistProfile?.signature?.dataUrl || null;

  const handleSaveSignature = async (dataUrl) => {
    setSignaturePadOpen(false);
    setSignatureBusy(true);
    try {
      await updateTherapistDocument(therapistId, {
        signature: { dataUrl, updatedAt: serverTimestamp() },
      });
      dispatch(
        setTherapistProfile({
          ...therapistProfile,
          signature: { dataUrl, updatedAt: new Date().toISOString() },
        })
      );
      showSuccess("החתימה נשמרה");
    } catch (error) {
      console.error("[CK-5060] Error saving therapist signature:", {
        errorCode: "CK-5060",
        errorMessage: error.message,
        therapistId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת החתימה");
    } finally {
      setSignatureBusy(false);
    }
  };

  const handleRemoveSignature = async () => {
    if (!savedSignatureUrl) return;
    if (!window.confirm("להסיר את החתימה השמורה? בייצוא הבא תתבקשו לחתום מחדש.")) return;
    setSignatureBusy(true);
    try {
      await updateTherapistDocument(therapistId, { signature: deleteField() });
      const next = { ...therapistProfile };
      delete next.signature;
      dispatch(setTherapistProfile(next));
      showSuccess("החתימה הוסרה");
    } catch (error) {
      console.error("[CK-5061] Error removing therapist signature:", {
        errorCode: "CK-5061",
        errorMessage: error.message,
        therapistId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהסרת החתימה");
    } finally {
      setSignatureBusy(false);
    }
  };
  const clinicId = useSelector(selectClinicId);
  const user = useSelector(selectUser);
  const { showSuccess, showError } = useToast();

  // Active tab state
  const [activeTab, setActiveTab] = useState("notifications");

  // Email link state
  const [showEmailLinkModal, setShowEmailLinkModal] = useState(false);
  const [emailLinkValue, setEmailLinkValue] = useState("");
  const [isLinkingEmail, setIsLinkingEmail] = useState(false);
  const [emailLinkSuccess, setEmailLinkSuccess] = useState(false);
  const [emailLinkError, setEmailLinkError] = useState("");

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savingField, setSavingField] = useState(null); // Track which field is saving
  const [notificationPrefs, setNotificationPrefs] = useState(
    therapistProfile?.notificationPreferences ||
      getDefaultNotificationPreferences()
  );
  const [smsNotificationPrefs, setSmsNotificationPrefs] = useState(
    therapistProfile?.smsNotificationPreferences ||
      getDefaultSmsNotificationPreferences()
  );
  const [pushPrefs, setPushPrefs] = useState(
    therapistProfile?.pushPreferences || getDefaultPushPreferences()
  );

  // Home page settings state (KEPT FOR DATA INTEGRITY - UI COMMENTED OUT)
  const [homePageSettings, setHomePageSettings] = useState(
    therapistProfile?.homePageSettings || getDefaultHomePageSettings()
  );
  /* const [isHomePageSectionExpanded, setIsHomePageSectionExpanded] =
    useState(true); */

  // Patient types state
  const [patientTypes, setPatientTypes] = useState(
    therapistProfile?.patientTypes || DEFAULT_PATIENT_TYPES
  );
  const [showPatientTypeModal, setShowPatientTypeModal] = useState(false);
  const [editingPatientType, setEditingPatientType] = useState(null);
  const [patientTypeForm, setPatientTypeForm] = useState({
    label: "",
    color: "#3b82f6",
  });

  // Master notifications toggle
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    therapistProfile?.notificationsEnabled !== false
  );

  // Calendar display preference — minimize the side menu when entering the
  // calendar page. Missing property = enabled (backward compatible).
  const [minimizeMenuOnCalendar, setMinimizeMenuOnCalendar] = useState(
    therapistProfile?.minimizeMenuOnCalendar !== false
  );

  // Push notifications state
  const [isPushSaving, setIsPushSaving] = useState(false);
  const [isSendingTestPush, setIsSendingTestPush] = useState(false);
  const pushSupported = isPushNotificationSupported();
  const pushPermission = getPushPermissionState();
  const pushEnabled = therapistProfile?.pushNotificationsEnabled === true;

  // Email link — show button only for whitelisted phones without a linked auth email
  const firebaseAuthEmail = auth.currentUser?.email;
  const showEmailLinkButton =
    EMAIL_LINK_ALLOWED_PHONES.includes(therapistProfile?.phone) &&
    !firebaseAuthEmail;

  useEffect(() => {
    if (showEmailLinkModal && therapistProfile?.email && !emailLinkValue) {
      setEmailLinkValue(therapistProfile.email);
    }
  }, [showEmailLinkModal, therapistProfile?.email]);

  const handleLinkEmail = useCallback(async () => {
    const trimmed = emailLinkValue.trim().toLowerCase();
    if (!trimmed) {
      setEmailLinkError("יש להזין כתובת מייל");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailLinkError("כתובת מייל לא תקינה");
      return;
    }

    setIsLinkingEmail(true);
    setEmailLinkError("");

    try {
      const { customToken } = await linkEmailToAuthUser(trimmed);
      await signInWithCustomToken(auth, customToken);

      if (therapistProfile?.uid) {
        await updateTherapistDocument(therapistProfile.uid, { email: trimmed });
        dispatch(
          setTherapistProfile({ ...therapistProfile, email: trimmed })
        );
      }

      dispatch(
        setUser({
          uid: auth.currentUser.uid,
          phoneNumber: auth.currentUser.phoneNumber,
          email: auth.currentUser.email || trimmed,
        })
      );

      setEmailLinkSuccess(true);
      showSuccess("המייל קושר בהצלחה!");
      setTimeout(() => {
        setShowEmailLinkModal(false);
        setEmailLinkSuccess(false);
        setEmailLinkValue("");
      }, 2000);
    } catch (err) {
      console.error("[CK-EMAIL-LINK] Error linking email:", err.message);
      if (
        err.message?.includes("already-exists") ||
        err.message?.includes("already in use")
      ) {
        setEmailLinkError("כתובת מייל זו כבר משויכת לחשבון אחר");
      } else {
        setEmailLinkError("שגיאה בקישור המייל. נסה שוב מאוחר יותר.");
      }
    } finally {
      setIsLinkingEmail(false);
    }
  }, [emailLinkValue, dispatch, therapistProfile, showSuccess]);

  // Initialize preferences from profile
  useEffect(() => {
    if (therapistProfile?.notificationPreferences) {
      const merged = {
        ...getDefaultNotificationPreferences(),
        ...therapistProfile.notificationPreferences,
      };
      setNotificationPrefs(merged);
    }

    if (therapistProfile?.smsNotificationPreferences) {
      const merged = {
        ...getDefaultSmsNotificationPreferences(),
        ...therapistProfile.smsNotificationPreferences,
      };
      setSmsNotificationPrefs(merged);
    }

    if (therapistProfile?.pushPreferences) {
      const merged = {
        ...getDefaultPushPreferences(),
        ...therapistProfile.pushPreferences,
      };
      setPushPrefs(merged);
    }

    // Initialize master notifications toggle
    if (therapistProfile?.notificationsEnabled !== undefined) {
      setNotificationsEnabled(therapistProfile.notificationsEnabled);
    } else {
      setNotificationsEnabled(true);
    }

    // Initialize calendar menu-minimize preference (missing = true)
    setMinimizeMenuOnCalendar(
      therapistProfile?.minimizeMenuOnCalendar !== false
    );

    // Initialize home page settings
    if (therapistProfile?.homePageSettings) {
      const defaultSettings = getDefaultHomePageSettings();
      const merged = {
        selectedStats:
          therapistProfile.homePageSettings.selectedStats ||
          defaultSettings.selectedStats,
        sections:
          therapistProfile.homePageSettings.sections ||
          defaultSettings.sections,
      };
      setHomePageSettings(merged);
    }

    // Initialize patient types
    if (therapistProfile?.patientTypes) {
      setPatientTypes(therapistProfile.patientTypes);
    }
  }, [therapistProfile]);

  // Handle toggle change for notification preferences
  const handleToggle = async (key) => {
    const newValue = !notificationPrefs[key];
    const updatedPrefs = {
      ...notificationPrefs,
      [key]: newValue,
    };

    setNotificationPrefs(updatedPrefs);

    // Save to Firebase
    setIsSaving(true);
    setSavingField("notifications");
    setSaveSuccess(false);

    try {
      await updateTherapistDocument(therapistId, {
        notificationPreferences: updatedPrefs,
      });

      // Update Redux store
      dispatch(
        setTherapistProfile({
          ...therapistProfile,
          notificationPreferences: updatedPrefs,
        })
      );

      setSaveSuccess(true);
      showSuccess("הגדרות ההתראות נשמרו בהצלחה");
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error("[CK-8600] Error saving notification preferences:", {
        errorCode: "CK-8600",
        errorMessage: error.message,
        therapistId,
        key,
        newValue,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הגדרות ההתראות");
      // Revert on error
      setNotificationPrefs((prev) => ({
        ...prev,
        [key]: !newValue,
      }));
    } finally {
      setIsSaving(false);
      setSavingField(null);
    }
  };

  const handleSmsToggle = async (key) => {
    const newValue = !smsNotificationPrefs[key];
    const updatedPrefs = {
      ...smsNotificationPrefs,
      [key]: newValue,
    };

    setSmsNotificationPrefs(updatedPrefs);

    setIsSaving(true);
    setSavingField("smsNotifications");
    setSaveSuccess(false);

    try {
      await updateTherapistDocument(therapistId, {
        smsNotificationPreferences: updatedPrefs,
      });

      dispatch(
        setTherapistProfile({
          ...therapistProfile,
          smsNotificationPreferences: updatedPrefs,
        })
      );

      setSaveSuccess(true);
      showSuccess("הגדרות התראות SMS נשמרו בהצלחה");
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error("[CK-8602] Error saving SMS notification preferences:", {
        errorCode: "CK-8602",
        errorMessage: error.message,
        therapistId,
        key,
        newValue,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הגדרות התראות SMS");
      setSmsNotificationPrefs((prev) => ({
        ...prev,
        [key]: !newValue,
      }));
    } finally {
      setIsSaving(false);
      setSavingField(null);
    }
  };

  const handleMasterNotificationsToggle = async () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);

    setIsSaving(true);
    setSavingField("masterNotifications");
    setSaveSuccess(false);

    try {
      await updateTherapistDocument(therapistId, {
        notificationsEnabled: newValue,
      });

      dispatch(
        setTherapistProfile({
          ...therapistProfile,
          notificationsEnabled: newValue,
        })
      );

      setSaveSuccess(true);
      showSuccess(newValue ? "התראות הופעלו" : "התראות כובו");
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error("[CK-8604] Error saving master notifications toggle:", {
        errorCode: "CK-8604",
        errorMessage: error.message,
        therapistId,
        newValue,
      });
      showError("שגיאה בשמירת הגדרות התראות");
      setNotificationsEnabled(!newValue);
    } finally {
      setIsSaving(false);
      setSavingField(null);
    }
  };

  const handleMinimizeMenuToggle = async () => {
    const newValue = !minimizeMenuOnCalendar;
    setMinimizeMenuOnCalendar(newValue);

    setIsSaving(true);
    setSavingField("minimizeMenuOnCalendar");
    setSaveSuccess(false);

    try {
      await updateTherapistDocument(therapistId, {
        minimizeMenuOnCalendar: newValue,
      });

      dispatch(
        setTherapistProfile({
          ...therapistProfile,
          minimizeMenuOnCalendar: newValue,
        })
      );

      setSaveSuccess(true);
      showSuccess("ההעדפה נשמרה");
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error("[CK-5620] Error saving calendar menu preference:", {
        errorCode: "CK-5620",
        errorMessage: error.message,
        therapistId,
        newValue,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת ההעדפה");
      setMinimizeMenuOnCalendar(!newValue);
    } finally {
      setIsSaving(false);
      setSavingField(null);
    }
  };

  const handlePushPrefToggle = async (key) => {
    const newValue = !pushPrefs[key];
    const updatedPrefs = { ...pushPrefs, [key]: newValue };

    setPushPrefs(updatedPrefs);

    setIsSaving(true);
    setSavingField("pushPreferences");
    setSaveSuccess(false);

    try {
      await updateTherapistDocument(therapistId, {
        pushPreferences: updatedPrefs,
      });

      dispatch(
        setTherapistProfile({
          ...therapistProfile,
          pushPreferences: updatedPrefs,
        })
      );

      setSaveSuccess(true);
      showSuccess("הגדרות התראות פוש נשמרו");
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error("[CK-8603] Error saving push preferences:", {
        errorCode: "CK-8603",
        errorMessage: error.message,
        therapistId,
        key,
        newValue,
      });
      showError("שגיאה בשמירת הגדרות פוש");
      setPushPrefs((prev) => ({ ...prev, [key]: !newValue }));
    } finally {
      setIsSaving(false);
      setSavingField(null);
    }
  };

  // ============================================
  // HOME PAGE SETTINGS HANDLERS (COMMENTED OUT)
  // ============================================
  /*
  // Save home page settings to Firebase
  const saveHomePageSettings = async (newSettings) => {
    setIsSaving(true);
    setSavingField("homepage");
    setSaveSuccess(false);

    try {
      await updateTherapistDocument(therapistId, {
        homePageSettings: newSettings,
      });

      // Update Redux store
      dispatch(
        setTherapistProfile({
          ...therapistProfile,
          homePageSettings: newSettings,
        })
      );

      setSaveSuccess(true);
      showSuccess("הגדרות עמוד הבית נשמרו בהצלחה");
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error("[CK-8601] Error saving home page settings:", {
        errorCode: "CK-8601",
        errorMessage: error.message,
        therapistId,
        newSettings,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת הגדרות עמוד הבית");
    } finally {
      setIsSaving(false);
      setSavingField(null);
    }
  };

  // Handle stat selection change
  const handleStatToggle = (statId) => {
    const currentStats = [...homePageSettings.selectedStats];
    const index = currentStats.indexOf(statId);

    if (index > -1) {
      // Remove if selected and we have more than 1
      if (currentStats.length > 1) {
        currentStats.splice(index, 1);
      }
    } else {
      // Add if not selected (max 4)
      if (currentStats.length < 4) {
        currentStats.push(statId);
      }
    }

    const newSettings = { ...homePageSettings, selectedStats: currentStats };
    setHomePageSettings(newSettings);
    saveHomePageSettings(newSettings);
  };

  // Handle section visibility toggle
  const handleSectionToggle = (sectionId) => {
    const newSections = homePageSettings.sections.map((section) =>
      section.id === sectionId
        ? { ...section, visible: !section.visible }
        : section
    );

    const newSettings = { ...homePageSettings, sections: newSections };
    setHomePageSettings(newSettings);
    saveHomePageSettings(newSettings);
  };

  // Handle section order change
  const handleMoveSection = (sectionId, direction) => {
    const sections = [...homePageSettings.sections];
    const currentIndex = sections.findIndex((s) => s.id === sectionId);

    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    // Check bounds
    if (newIndex < 0 || newIndex >= sections.length) return;

    // Swap
    [sections[currentIndex], sections[newIndex]] = [
      sections[newIndex],
      sections[currentIndex],
    ];

    const newSettings = { ...homePageSettings, sections };
    setHomePageSettings(newSettings);
    saveHomePageSettings(newSettings);
  };
  */

  // ============================================
  // LEAD STATUS HANDLERS
  // ============================================

  // Patient type colors (same as lead status colors)
  const PATIENT_TYPE_COLORS = [
    "#3b82f6", // Blue
    "#22c55e", // Green
    "#f59e0b", // Orange
    "#ef4444", // Red
    "#8b5cf6", // Purple
    "#ec4899", // Pink
    "#06b6d4", // Cyan
    "#6b7280", // Gray
  ];

  const handleOpenPatientTypeModal = (type = null) => {
    if (type) {
      setEditingPatientType(type);
      setPatientTypeForm({
        label: type.label,
        color: type.color,
      });
    } else {
      setEditingPatientType(null);
      setPatientTypeForm({
        label: "",
        color: "#3b82f6",
      });
    }
    setShowPatientTypeModal(true);
  };

  const handleClosePatientTypeModal = () => {
    setShowPatientTypeModal(false);
    setEditingPatientType(null);
    setPatientTypeForm({
      label: "",
      color: "#3b82f6",
    });
  };

  const handleSavePatientType = async () => {
    if (!patientTypeForm.label.trim()) return;

    let newTypes;
    if (editingPatientType) {
      // Update existing type
      newTypes = patientTypes.map((t) =>
        t.id === editingPatientType.id
          ? {
              ...t,
              label: patientTypeForm.label.trim(),
              color: patientTypeForm.color,
            }
          : t
      );
    } else {
      // Create new type
      const newId = `pt_${Date.now()}`;
      const maxOrder =
        patientTypes.length > 0
          ? Math.max(...patientTypes.map((t) => t.order))
          : -1;
      newTypes = [
        ...patientTypes,
        {
          id: newId,
          label: patientTypeForm.label.trim(),
          color: patientTypeForm.color,
          order: maxOrder + 1,
        },
      ];
    }

    setPatientTypes(newTypes);
    await savePatientTypes(newTypes);
    handleClosePatientTypeModal();
  };

  const handleDeletePatientType = async (typeToDelete) => {
    const newTypes = patientTypes.filter((t) => t.id !== typeToDelete.id);
    setPatientTypes(newTypes);
    await savePatientTypes(newTypes);
  };

  const handleMovePatientType = async (typeId, direction) => {
    const types = [...patientTypes];
    const currentIndex = types.findIndex((t) => t.id === typeId);

    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    // Check bounds
    if (newIndex < 0 || newIndex >= types.length) return;

    // Swap
    [types[currentIndex], types[newIndex]] = [
      types[newIndex],
      types[currentIndex],
    ];

    // Update order numbers
    types.forEach((t, i) => {
      t.order = i;
    });

    setPatientTypes(types);
    await savePatientTypes(types);
  };

  const savePatientTypes = async (newTypes) => {
    setIsSaving(true);
    setSavingField("patientTypes");
    setSaveSuccess(false);

    try {
      await updateTherapistDocument(therapistId, {
        patientTypes: newTypes,
      });

      // Update Redux store
      dispatch(
        setTherapistProfile({
          ...therapistProfile,
          patientTypes: newTypes,
        })
      );

      setSaveSuccess(true);
      showSuccess("סוגי המטופלים נשמרו בהצלחה");
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error("[CK-8603] Error saving patient types:", {
        errorCode: "CK-8603",
        errorMessage: error.message,
        therapistId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת סוגי המטופלים");
    } finally {
      setIsSaving(false);
      setSavingField(null);
    }
  };

  // ============================================
  // PUSH NOTIFICATION HANDLERS
  // ============================================

  const handleEnablePush = async () => {
    if (!therapistId || !clinicId) return;
    setIsPushSaving(true);
    try {
      const result = await requestPushPermissionAndSaveToken(therapistId, clinicId);
      if (result.success) {
        dispatch(
          setTherapistProfile({
            ...therapistProfile,
            pushNotificationsEnabled: true,
          })
        );
        showSuccess("התראות פוש הופעלו בהצלחה!");
      } else if (result.permission === "denied") {
        showError("ההרשאה להתראות נחסמה בדפדפן. יש לשנות זאת בהגדרות הדפדפן/מכשיר.");
      } else if (result.permission === "unsupported") {
        showError("הדפדפן שלך לא תומך בהתראות פוש.");
      } else {
        showError("לא הצלחנו להפעיל התראות פוש. נסה שוב מאוחר יותר.");
      }
    } catch {
      showError("שגיאה בהפעלת התראות פוש.");
    } finally {
      setIsPushSaving(false);
    }
  };

  const handleDisablePush = async () => {
    if (!therapistId) return;
    setIsPushSaving(true);
    try {
      await disablePushNotifications(therapistId);
      dispatch(
        setTherapistProfile({
          ...therapistProfile,
          pushNotificationsEnabled: false,
        })
      );
      showSuccess("התראות פוש כובו.");
    } catch {
      showError("שגיאה בכיבוי התראות פוש.");
    } finally {
      setIsPushSaving(false);
    }
  };

  const handleSendTestPush = async () => {
    setIsSendingTestPush(true);
    try {
      const result = await sendTestPushNotification();
      if (result?.successCount > 0) {
        showSuccess("התראת בדיקה נשלחה! בדוק את המכשיר שלך.");
      } else {
        showError(
          "לא נמצאו מכשירים רשומים להתראות פוש. יש להפעיל התראות פוש במכשיר תחילה."
        );
      }
    } catch {
      showError("שגיאה בשליחת התראת הבדיקה. נסה שוב מאוחר יותר.");
    } finally {
      setIsSendingTestPush(false);
    }
  };

  return (
    <div className="settings-page">
      {/* Header */}
      <div className="settings-header">
        <div className="header-title">
          <UserCircle size={28} weight="duotone" className="header-icon" />
          <h1>הגדרות מטפל</h1>
        </div>
        <HelpButton featureKey="settings" />
      </div>

      {/* Profile Summary Card */}
      <div className="profile-summary-card">
        <div className="profile-summary-avatar">
          {therapistProfile?.firstName?.charAt(0)}
          {therapistProfile?.lastName?.charAt(0)}
        </div>
        <div className="profile-summary-info">
          <span className="profile-summary-name">
            {therapistProfile?.firstName} {therapistProfile?.lastName}
          </span>
          <span className="profile-summary-phone" dir="ltr">
            {therapistProfile?.phone}
          </span>
        </div>
        {showEmailLinkButton && (
          <button
            className="email-link-settings-btn"
            onClick={() => setShowEmailLinkModal(true)}
          >
            <EnvelopeSimple size={16} weight="duotone" />
            <span>קשר מייל</span>
          </button>
        )}
      </div>

      {/* Main Tabs */}
      <div className="settings-tabs">
        <button
          className={`settings-tab ${
            activeTab === "notifications" ? "active" : ""
          }`}
          onClick={() => setActiveTab("notifications")}
        >
          <Bell size={20} weight="regular" />
          <span>ניהול התראות</span>
        </button>
        <button
          className={`settings-tab ${activeTab === "tags" ? "active" : ""}`}
          onClick={() => setActiveTab("tags")}
        >
          <Tag size={20} weight="regular" />
          <span>ניהול תגים</span>
        </button>
        <button
          className={`settings-tab ${activeTab === "display" ? "active" : ""}`}
          onClick={() => setActiveTab("display")}
        >
          <CalendarCheck size={20} weight="regular" />
          <span>העדפות תצוגה</span>
        </button>
      </div>

      <div className="settings-container">
        <div className="settings-content">
          {/* ============================================
              TAB 1: NOTIFICATIONS
              ============================================ */}
          {activeTab === "notifications" && (
            <div className="tab-content notifications-tab">
              <div className="customization-grid">
                {Object.entries(NOTIFICATION_CATEGORIES).map(
                  ([categoryKey, category]) => (
                    <div key={categoryKey} className="customization-card">
                      <div className="card-header">
                        <div className={`card-icon ${categoryKey}`}>
                          <category.icon size={22} weight="duotone" />
                        </div>
                        <div className="card-title">
                          <h3>{category.label}</h3>
                          <p>התאם את ההתראות שתרצה לקבל</p>
                        </div>
                        {(isSaving || saveSuccess) &&
                          savingField === "notifications" && (
                            <div
                              className={`card-save-indicator ${
                                saveSuccess ? "success" : ""
                              }`}
                            >
                              {isSaving ? (
                                <LoaderLoop size="xs" />
                              ) : (
                                <CheckCircle size={14} weight="fill" />
                              )}
                            </div>
                          )}
                      </div>

                      <div className="card-content">
                        <div className="notification-items-list">
                          {category.keys.map((prefKey) => {
                            const pref = NOTIFICATION_PREFERENCES[prefKey];
                            if (!pref) return null;

                            const isEnabled =
                              notificationPrefs[prefKey] ?? pref.defaultEnabled;

                            return (
                              <div
                                key={prefKey}
                                className={`notification-toggle-item ${
                                  isEnabled ? "enabled" : "disabled"
                                }`}
                              >
                                <div className="notification-info">
                                  <span className="notification-label">
                                    {pref.label}
                                  </span>
                                  <span className="notification-description">
                                    {pref.description}
                                  </span>
                                </div>

                                <button
                                  className={`toggle-btn ${
                                    isEnabled ? "on" : "off"
                                  }`}
                                  onClick={() => handleToggle(prefKey)}
                                  disabled={isSaving}
                                  aria-label={
                                    isEnabled ? "כבה התראה" : "הפעל התראה"
                                  }
                                >
                                  <span className="toggle-track">
                                    <span className="toggle-thumb" />
                                  </span>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )
                )}

                {/* Therapist Alerts Management Card — Master Toggle + Channels */}
                <div className={`customization-card alerts-management-card ${notificationsEnabled ? "master-enabled" : "master-disabled"}`}>
                  <div className="card-header">
                    <div className="card-icon alerts">
                      <BellRinging size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>ניהול התראות למטפל</h3>
                      <p>{notificationsEnabled ? "התראות פעילות — תקבל עדכונים בערוצים שהגדרת" : "התראות כבויות — לא יישלחו התראות"}</p>
                    </div>
                    {(isSaving || saveSuccess) &&
                      (savingField === "masterNotifications" || savingField === "smsNotifications") && (
                        <div
                          className={`card-save-indicator ${
                            saveSuccess ? "success" : ""
                          }`}
                        >
                          {isSaving ? (
                            <LoaderLoop size="xs" />
                          ) : (
                            <CheckCircle size={14} weight="fill" />
                          )}
                        </div>
                      )}
                    <button
                      className={`toggle-btn ${notificationsEnabled ? "on" : "off"}`}
                      onClick={handleMasterNotificationsToggle}
                      disabled={isSaving}
                      aria-label={notificationsEnabled ? "כבה את כל ההתראות" : "הפעל התראות"}
                    >
                      <span className="toggle-track">
                        <span className="toggle-thumb" />
                      </span>
                    </button>
                  </div>

                  {notificationsEnabled && (
                    <div className="card-content">
                      {/* SMS Section */}
                      <div className="alerts-channel-section">
                        <div className="alerts-channel-header">
                          <ChatText size={18} weight="duotone" />
                          <span>התראות SMS</span>
                        </div>
                        <div className="alerts-channel-notice sms-notice">
                          <ChatText size={14} weight="fill" />
                          <span>הודעות SMS נשלחות למספר הטלפון הרשום בפרופיל שלך ומנוכות ממכסת ה-SMS של הקליניקה</span>
                        </div>
                        <div className="notification-items-list">
                          {Object.values(SMS_NOTIFICATION_PREFERENCES).map(
                            (pref) => {
                              const isEnabled =
                                smsNotificationPrefs[pref.key] ??
                                pref.defaultEnabled;

                              return (
                                <div
                                  key={pref.key}
                                  className={`notification-toggle-item ${
                                    isEnabled ? "enabled" : "disabled"
                                  }`}
                                >
                                  <div className="notification-info">
                                    <span className="notification-label">
                                      {pref.label}
                                    </span>
                                    <span className="notification-description">
                                      {pref.description}
                                    </span>
                                  </div>

                                  <button
                                    className={`toggle-btn ${
                                      isEnabled ? "on" : "off"
                                    }`}
                                    onClick={() => handleSmsToggle(pref.key)}
                                    disabled={isSaving}
                                    aria-label={
                                      isEnabled
                                        ? "כבה התראת SMS"
                                        : "הפעל התראת SMS"
                                    }
                                  >
                                    <span className="toggle-track">
                                      <span className="toggle-thumb" />
                                    </span>
                                  </button>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>

                      {/* Push on/off Section */}
                      <div className="alerts-channel-section">
                        <div className="alerts-channel-header">
                          <DeviceMobile size={18} weight="duotone" />
                          <span>התראות פוש</span>
                        </div>
                        <div className="alerts-channel-notice push-notice">
                          <DeviceMobile size={14} weight="fill" />
                          <span>התראות קופצות במכשיר הנייד למי שהוסיף את האפליקציה למסך הבית ואישר קבלת התראות. צריכים עזרה? פנו לתמיכה.</span>
                        </div>
                        {!pushSupported ? (
                          <div className="alerts-channel-notice push-unsupported-notice">
                            <DeviceMobile size={14} weight="fill" />
                            <span>
                              הדפדפן או המכשיר שלך לא תומך בהתראות פוש.
                              {/iPhone|iPad/.test(navigator.userAgent) &&
                                " ב-iOS, יש להוסיף את האפליקציה למסך הבית (Add to Home Screen) ולהפעיל התראות משם."}
                            </span>
                          </div>
                        ) : pushPermission === "denied" ? (
                          <div className="alerts-channel-notice push-denied-notice">
                            <DeviceMobile size={14} weight="fill" />
                            <span>
                              ההרשאה להתראות נחסמה בדפדפן. כדי להפעיל, יש לשנות את
                              הגדרות ההתראות בדפדפן או במכשיר ולרענן את הדף.
                            </span>
                          </div>
                        ) : (
                          <div className="notification-items-list">
                            <div className={`notification-toggle-item ${pushEnabled ? "enabled" : "disabled"}`}>
                              <div className="notification-info">
                                <span className="notification-label">
                                  {pushEnabled ? "התראות פוש פעילות" : "התראות פוש כבויות"}
                                </span>
                                <span className="notification-description">
                                  {pushEnabled
                                    ? "לחץ לכיבוי התראות פוש במכשיר זה"
                                    : "לחץ להפעלת התראות פוש במכשיר זה"}
                                </span>
                              </div>
                              <button
                                className={`toggle-btn ${pushEnabled ? "on" : "off"}`}
                                onClick={pushEnabled ? handleDisablePush : handleEnablePush}
                                disabled={isPushSaving}
                                aria-label={pushEnabled ? "כבה התראות פוש" : "הפעל התראות פוש"}
                              >
                                <span className="toggle-track">
                                  <span className="toggle-thumb" />
                                </span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Push Preferences Card — always visible (prefs are per-therapist,
                    not per-device) so they can be managed from desktop too */}
                {notificationsEnabled && (
                  <div className={`customization-card push-preferences-card ${pushEnabled ? "" : "push-off"}`}>
                    <div className="card-header">
                      <div className="card-icon push">
                        <DeviceMobile size={22} weight="duotone" />
                      </div>
                      <div className="card-title">
                        <h3>העדפות פוש</h3>
                        <p>
                          {pushEnabled
                            ? "בחר אילו התראות פוש תרצה לקבל"
                            : "ההעדפות נשמרות לכל המכשירים — יישלחו ברגע שתפעיל התראות פוש במכשיר"}
                        </p>
                      </div>
                      {(isSaving || saveSuccess) &&
                        savingField === "pushPreferences" && (
                          <div
                            className={`card-save-indicator ${
                              saveSuccess ? "success" : ""
                            }`}
                          >
                            {isSaving ? (
                              <LoaderLoop size="xs" />
                            ) : (
                              <CheckCircle size={14} weight="fill" />
                            )}
                          </div>
                        )}
                    </div>

                    <div className="card-content">
                      <div className="push-prefs-section">
                        {Object.entries(PUSH_PREF_CATEGORIES)
                          .filter(
                            ([, category]) =>
                              !category.nutritionOnly || isNutritionClinic
                          )
                          .map(([catKey, category]) => (
                          <div key={catKey} className="push-pref-category">
                            <h4 className="push-pref-category-label">{category.label}</h4>
                            {category.keys.map((prefKey) => {
                              const pref = PUSH_PREFERENCES[prefKey];
                              if (!pref) return null;
                              const isOn = pushPrefs[prefKey] ?? pref.defaultEnabled;
                              return (
                                <div
                                  key={prefKey}
                                  className={`notification-toggle-item ${isOn ? "enabled" : "disabled"}`}
                                >
                                  <div className="notification-info">
                                    <span className="notification-label">{pref.label}</span>
                                    <span className="notification-description">{pref.description}</span>
                                  </div>
                                  <button
                                    className={`toggle-btn ${isOn ? "on" : "off"}`}
                                    onClick={() => handlePushPrefToggle(prefKey)}
                                    disabled={isSaving}
                                    aria-label={isOn ? `כבה ${pref.label}` : `הפעל ${pref.label}`}
                                  >
                                    <span className="toggle-track">
                                      <span className="toggle-thumb" />
                                    </span>
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                className="test-push-btn"
                onClick={handleSendTestPush}
                disabled={isSendingTestPush}
              >
                {isSendingTestPush ? (
                  <LoaderLoop size="xs" />
                ) : (
                  <PaperPlaneTilt size={18} weight="duotone" />
                )}
                <span>
                  {isSendingTestPush
                    ? "שולח התראת בדיקה..."
                    : "שלח התראת פוש לבדיקה"}
                </span>
              </button>
            </div>
          )}

          {/* ============================================
              TAB: DISPLAY PREFERENCES
              ============================================ */}
          {activeTab === "display" && (
            <div className="tab-content display-tab">
              <div className="customization-grid">
                <div className="customization-card">
                  <div className="card-header">
                    <div className="card-icon appointments">
                      <CalendarCheck size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>יומן ותורים</h3>
                      <p>התאם את חוויית התצוגה בעמוד היומן</p>
                    </div>
                    {(isSaving || saveSuccess) &&
                      savingField === "minimizeMenuOnCalendar" && (
                        <div
                          className={`card-save-indicator ${
                            saveSuccess ? "success" : ""
                          }`}
                        >
                          {isSaving ? (
                            <LoaderLoop size="xs" />
                          ) : (
                            <CheckCircle size={14} weight="fill" />
                          )}
                        </div>
                      )}
                  </div>
                  <div className="card-content">
                    <div className="notification-items-list">
                      <div
                        className={`notification-toggle-item ${
                          minimizeMenuOnCalendar ? "enabled" : "disabled"
                        }`}
                      >
                        <div className="notification-info">
                          <span className="notification-label">
                            מזעור תפריט הצד בכניסה ליומן
                          </span>
                          <span className="notification-description">
                            בעת מעבר לעמוד היומן, תפריט הניווט הצדדי יתמזער
                            אוטומטית כדי לפנות מקום ליומן
                          </span>
                        </div>
                        <button
                          className={`toggle-btn ${
                            minimizeMenuOnCalendar ? "on" : "off"
                          }`}
                          onClick={handleMinimizeMenuToggle}
                          disabled={isSaving}
                          aria-label={
                            minimizeMenuOnCalendar
                              ? "כבה מזעור תפריט אוטומטי"
                              : "הפעל מזעור תפריט אוטומטי"
                          }
                        >
                          <span className="toggle-track">
                            <span className="toggle-thumb" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Saved signature — reused on signed PDFs (AI treatments summary, forms) */}
                <div className="customization-card">
                  <div className="card-header">
                    <div className="card-icon treatments">
                      <PenNib size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>החתימה שלי</h3>
                      <p>חתימה שמורה שמוטבעת על מסמכי PDF חתומים</p>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="signature-setting">
                      {savedSignatureUrl ? (
                        <img
                          className="signature-setting-preview"
                          src={savedSignatureUrl}
                          alt="החתימה השמורה שלך"
                        />
                      ) : (
                        <div className="signature-setting-empty">
                          עדיין אין חתימה שמורה. ציירו אותה פעם אחת והיא תשמש אתכם בכל ייצוא חתום.
                        </div>
                      )}
                      <div className="signature-setting-actions">
                        <button
                          type="button"
                          className="signature-setting-btn primary"
                          onClick={() => setSignaturePadOpen(true)}
                          disabled={signatureBusy}
                        >
                          <PenNib size={15} weight="fill" />
                          {savedSignatureUrl ? "החלפת חתימה" : "ציור חתימה"}
                        </button>
                        {savedSignatureUrl && (
                          <button
                            type="button"
                            className="signature-setting-btn danger"
                            onClick={handleRemoveSignature}
                            disabled={signatureBusy}
                          >
                            <Trash size={15} />
                            הסרה
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============================================
              TAB 3: TAGS MANAGEMENT
              ============================================ */}
          {activeTab === "tags" && (
            <div className="tab-content tags-tab">
              <div className="customization-grid">
                {/* Lead Statuses Card — Leads v2: statuses are clinic-wide and managed from the מתעניינים tab */}
                <div className="customization-card">
                  <div className="card-header">
                    <div className="card-icon leads">
                      <UserPlus size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>סטטוסי מתעניינים</h3>
                      <p>הסטטוסים משותפים לכל הקליניקה ומנוהלים מלשונית מתעניינים בארגז הכלים</p>
                    </div>
                  </div>
                  <div className="card-body">
                    <button
                      type="button"
                      className="reset-btn"
                      onClick={() => navigate("/dashboard/tasks?tab=leads")}
                    >
                      לניהול הסטטוסים במתעניינים
                    </button>
                  </div>
                </div>

                {/* Patient Types Card */}
                <div className="customization-card">
                  <div className="card-header">
                    <div className="card-icon patients">
                      <UsersThree size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>סוגי מטופלים</h3>
                      <p>הגדר סוגי מטופלים לסיווג וסינון</p>
                    </div>
                    <button
                      className="add-item-btn"
                      onClick={() => handleOpenPatientTypeModal()}
                    >
                      <Plus size={16} weight="bold" />
                    </button>
                  </div>

                  <div className="card-content">
                    {(isSaving || saveSuccess) &&
                      savingField === "patientTypes" && (
                        <div
                          className={`card-save-indicator inline ${
                            saveSuccess ? "success" : ""
                          }`}
                        >
                          {isSaving ? (
                            <>
                              <LoaderLoop size="xs" />
                              <span>שומר...</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle size={14} weight="fill" />
                              <span>נשמר!</span>
                            </>
                          )}
                        </div>
                      )}

                    {patientTypes.length === 0 ? (
                      <div className="card-empty">
                        <UsersThree size={28} weight="duotone" />
                        <p>לא הוגדרו סוגי מטופלים</p>
                        <span>הוסף סוגי מטופלים לסיווג וסינון קל יותר</span>
                      </div>
                    ) : (
                      <div className="items-list">
                        {[...patientTypes]
                          .sort((a, b) => a.order - b.order)
                          .map((type, index) => {
                            return (
                              <div key={type.id} className="item-tag">
                                <span
                                  className="item-color"
                                  style={{ backgroundColor: type.color }}
                                />
                                <span className="item-name">{type.label}</span>
                                <div className="item-actions">
                                  <button
                                    onClick={() =>
                                      handleMovePatientType(type.id, "up")
                                    }
                                    disabled={index === 0 || isSaving}
                                    title="הזז למעלה"
                                  >
                                    <ArrowUp size={14} weight="bold" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleMovePatientType(type.id, "down")
                                    }
                                    disabled={
                                      index === patientTypes.length - 1 ||
                                      isSaving
                                    }
                                    title="הזז למטה"
                                  >
                                    <ArrowDown size={14} weight="bold" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleOpenPatientTypeModal(type)
                                    }
                                    disabled={isSaving}
                                    title="ערוך"
                                  >
                                    <Pencil size={14} />
                                  </button>
                                  <button
                                    className="delete"
                                    onClick={() =>
                                      handleDeletePatientType(type)
                                    }
                                    disabled={isSaving}
                                    title="מחק"
                                  >
                                    <Trash size={14} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Patient Type Modal */}
      {showPatientTypeModal && (
        <div className="modal-overlay" onClick={handleClosePatientTypeModal}>
          <div
            className="modal patient-type-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>
                {editingPatientType ? "עריכת סוג מטופל" : "סוג מטופל חדש"}
              </h3>
              <ModalCloseButton onClick={handleClosePatientTypeModal} />
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="required">שם הסוג</label>
                <input
                  type="text"
                  value={patientTypeForm.label}
                  onChange={(e) =>
                    setPatientTypeForm({
                      ...patientTypeForm,
                      label: e.target.value,
                    })
                  }
                  placeholder="הכנס שם לסוג מטופל"
                />
              </div>

              <div className="form-group">
                <label>צבע</label>
                <div className="color-picker">
                  {PATIENT_TYPE_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`color-option ${
                        patientTypeForm.color === color ? "selected" : ""
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() =>
                        setPatientTypeForm({ ...patientTypeForm, color })
                      }
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={handleClosePatientTypeModal}
              >
                ביטול
              </button>
              <button
                className="btn-primary"
                onClick={handleSavePatientType}
                disabled={!patientTypeForm.label.trim() || isSaving}
              >
                {isSaving && <LoaderLoop size="xs" />}
                <span>{editingPatientType ? "שמור שינויים" : "צור סוג"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Link Modal */}
      {showEmailLinkModal && (
        <div
          className="email-link-settings-overlay"
          onClick={() => !isLinkingEmail && setShowEmailLinkModal(false)}
        >
          <div
            className="email-link-settings-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalCloseButton
              onClick={() => !isLinkingEmail && setShowEmailLinkModal(false)}
            />

            {emailLinkSuccess ? (
              <div className="email-link-settings-success">
                <div className="email-link-settings-success-icon">
                  <CheckCircle size={48} weight="duotone" />
                </div>
                <h3>המייל קושר בהצלחה!</h3>
                <p>מעכשיו תוכל להתחבר גם באמצעות כתובת המייל שלך.</p>
              </div>
            ) : (
              <>
                <div className="email-link-settings-icon">
                  <EnvelopeSimple size={32} weight="duotone" />
                </div>

                <h2 className="email-link-settings-title">קישור כתובת מייל</h2>

                <p className="email-link-settings-description">
                  הוסף כתובת מייל לחשבונך כדי שתוכל להתחבר גם באמצעות מייל.
                </p>

                <div className="email-link-settings-input-group">
                  <input
                    type="email"
                    className={`email-link-settings-input ${emailLinkError ? "has-error" : ""}`}
                    placeholder="הכנס כתובת מייל"
                    value={emailLinkValue}
                    onChange={(e) => {
                      setEmailLinkValue(e.target.value);
                      if (emailLinkError) setEmailLinkError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isLinkingEmail) handleLinkEmail();
                    }}
                    disabled={isLinkingEmail}
                    autoFocus
                    dir="ltr"
                  />
                  {emailLinkError && (
                    <span className="email-link-settings-error">{emailLinkError}</span>
                  )}
                </div>

                <div className="email-link-settings-actions">
                  <button
                    className="email-link-settings-submit"
                    onClick={handleLinkEmail}
                    disabled={isLinkingEmail || !emailLinkValue.trim()}
                  >
                    {isLinkingEmail ? (
                      <>
                        <LoaderLoop size="xs" />
                        <span>מקשר...</span>
                      </>
                    ) : (
                      <>
                        <EnvelopeSimple size={18} weight="bold" />
                        <span>קשר מייל</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {signaturePadOpen && (
        <SignaturePadModal
          title="החתימה שלי"
          hint="חתמו באמצעות העכבר או מסך מגע. החתימה תישמר בפרופיל שלך."
          confirmLabel="שמירה"
          onConfirm={handleSaveSignature}
          onClose={() => setSignaturePadOpen(false)}
        />
      )}
    </div>
  );
}

export default SettingsPage;
